import { useState } from "react";
import { Sparkles, Loader2, CheckCircle2, X, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";

/**
 * Phase 4.21 — KBA-Lookup-Button.
 * Sucht via Gemini die offiziellen Stammdaten zu HSN/TSN und mergt sie
 * (nach Bestätigung des Nutzers) ins Formular.
 */
export default function KbaLookupButton({ hsn, tsn, firstRegistrationDate, expectedBrand, expectedModel, currentDisplacement, currentPowerKw, onApply, label, compact }) {
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    // Feb-2026 (Bug-Fix): TSN kann 3 (alt) oder bis 12 Zeichen (neu, ab 2010) lang
    // sein. Vorher wurde der Button nur bei genau 3 Zeichen aktiv → moderne
    // 9-12-stellige TSNs blockierten die Funktion vollständig.
    const valid = hsn?.length === 4 && tsn?.length >= 3 && tsn?.length <= 12;
    // Jul-2026 (User-Wunsch) — Tooltip am ausgegrauten Button: welche Daten fehlen noch?
    const missing = [];
    if (!(hsn?.length === 4)) missing.push("HSN (4-stellig)");
    if (!(tsn?.length >= 3 && tsn?.length <= 12)) missing.push("TSN (3–12 Zeichen)");
    const disabledHint = missing.length
        ? `Zum Starten der KBA-Abfrage fehlt noch: ${missing.join(" · ")}`
        : "";

    const fetchData = async () => {
        if (!valid) {
            toast.error("HSN (4-stellig) und TSN (3-12 Zeichen) müssen ausgefüllt sein");
            return;
        }
        setLoading(true);
        try {
            // Feb-2026 Iter 28 (User-Wunsch 25 May, Update v6): Marke + Modell
            // aus dem ZB1-Scan / manueller Eingabe als Disambiguierungs-Anker
            // an das Backend mitsenden → verhindert Hersteller-Halluzinationen
            // (z. B. „Land Rover Discovery" bei einer Piaggio Vespa).
            const r = await api.post("/vehicles/kba-lookup", {
                hsn,
                tsn,
                first_registration_date: firstRegistrationDate || null,
                expected_brand: (expectedBrand || "").trim() || null,
                expected_model: (expectedModel || "").trim() || null,
            });
            if (!r.data.ok) {
                // Backend-Fehler wie „brand_mismatch" oder „low_confidence"
                // führen schon zu Toast — Warning bevorzugt anzeigen.
                const msg = r.data.warning || r.data.error || "KBA-Lookup fehlgeschlagen";
                if (r.data.error === "brand_mismatch") {
                    toast.error(msg, { duration: 8000 });
                } else if (r.data.error === "low_confidence") {
                    toast.warning(msg, { duration: 6000 });
                } else {
                    toast.error(msg);
                }
                return;
            }
            const filled = Object.entries(r.data.data || {}).filter(([k, v]) => k !== "confidence" && v != null && v !== "").length;
            if (filled === 0) {
                toast.message("Keine Daten gefunden für diese HSN/TSN-Kombination");
                return;
            }
            setResult(r.data);
            toast.success(`KBA: ${filled} Felder gefunden (${Math.round((r.data.data.confidence || 0) * 100)}% Konfidenz)${String(r.data.source || "").startsWith("intern") ? " · Quelle: interne Datenbank" : r.data.source === "ki-gelernt" ? " · in interne Datenbank übernommen" : ""}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "KBA-Lookup fehlgeschlagen");
        } finally {
            setLoading(false);
        }
    };

    const apply = () => {
        if (!result?.data) return;
        const d = result.data;
        // Brand casing normalisieren (Gemini liefert oft UPPERCASE wie "PIAGGIO")
        const normBrand = d.brand
            ? d.brand.charAt(0).toUpperCase() + d.brand.slice(1).toLowerCase()
            : d.brand;
        // Feb-2026 Iter 28 (User-Wunsch 25 May, Update v11): Modell ebenfalls
        // Title-Case (z. B. „VESPA GTS" → „Vespa GTS") — Eingabefeld + Templates
        // erwarten lesbares Case.
        const normModel = d.model_variant
            ? String(d.model_variant)
                .split(/\s+/)
                .map((w) => w.length <= 3 ? w : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                .join(" ")
            : d.model_variant;
        const mapped = {
            brand: normBrand,
            manufacturer: d.manufacturer,
            model: normModel,
            vehicle_class: d.vehicle_class,
            displacement: d.displacement,
            power_kw: d.power_kw,
            power_hp: d.power_hp,
            max_speed_kmh: d.top_speed_kmh,
            emission_class: d.emission_class,
            fuel: d.fuel,
        };
        onApply?.(mapped, d.common_options || []);
        toast.success("KBA-Daten ins Formular übernommen");
        setResult(null);
    };

    return (
        <div data-testid="kba-lookup-wrap">
            <span title={!valid ? disabledHint : ""} data-testid="kba-lookup-tooltip-wrap" className="inline-block">
            <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={loading || !valid}
                onClick={fetchData}
                title={!valid ? disabledHint : ""}
                className={`inline-flex items-center gap-1 font-bold uppercase tracking-wider rounded-sm bg-gray-100 hover:bg-gray-200 text-[#111827] border border-gray-300 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed ${compact ? "h-6 px-1.5 text-[9px]" : "px-2 py-1 text-[10px] h-auto"}`}
                data-testid="kba-lookup-btn"
            >
                {loading ? <Loader2 className={`${compact ? "w-2.5 h-2.5" : "w-3 h-3"} animate-spin`} /> : <Sparkles className={`${compact ? "w-2.5 h-2.5" : "w-3 h-3"}`} />}
                {loading ? "Suche…" : (label || "KBA-Daten anreichern")}
            </Button>
            </span>

            {result && (
                <div className="mt-2 bg-white border border-gray-200 rounded-sm p-3" data-testid="kba-result-card">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-bold text-gray-700">
                            {result.data.brand} {result.data.model_variant}
                            {Array.isArray(result.data.model_year_range) && result.data.model_year_range[0] && (
                                <span className="ml-1 text-gray-500">({result.data.model_year_range[0]}–{result.data.model_year_range[1] && result.data.model_year_range[1] !== "NULL" ? result.data.model_year_range[1] : "heute"})</span>
                            )}
                        </div>
                        <button type="button" onClick={() => setResult(null)} className="text-gray-400 hover:text-gray-900"><X className="w-3.5 h-3.5" /></button>
                    </div>
                    <ul className="text-[11px] text-gray-700 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5 mb-2">
                        {result.data.displacement && (
                            <li>
                                <span className="text-gray-500">Hubraum (P.1):</span>{" "}
                                <span className="font-bold font-mono-num">{result.data.displacement} cm³</span>
                                {/* Feb-2026 Iter 28 (User-Wunsch 25 May, Update v8/v9) — Vergleich
                                    + Diff-Pfeil mit dem aktuell hinterlegten Wert. */}
                                {currentDisplacement && Number(currentDisplacement) !== Number(result.data.displacement) && (
                                    <span className="ml-1 text-[10px] text-amber-700 font-mono-num inline-flex items-center gap-0.5">
                                        {Number(result.data.displacement) > Number(currentDisplacement)
                                            ? <ArrowUp className="w-2.5 h-2.5" />
                                            : <ArrowDown className="w-2.5 h-2.5" />}
                                        (hinterlegt: {currentDisplacement} cm³)
                                    </span>
                                )}
                            </li>
                        )}
                        {result.data.power_kw && (
                            <li>
                                <span className="text-gray-500">Leistung (P.2):</span>{" "}
                                <span className="font-bold font-mono-num">{result.data.power_kw} kW / {result.data.power_hp} PS</span>
                                {currentPowerKw && Number(currentPowerKw) !== Number(result.data.power_kw) && (
                                    <span className="ml-1 text-[10px] text-amber-700 font-mono-num inline-flex items-center gap-0.5">
                                        {Number(result.data.power_kw) > Number(currentPowerKw)
                                            ? <ArrowUp className="w-2.5 h-2.5" />
                                            : <ArrowDown className="w-2.5 h-2.5" />}
                                        (hinterlegt: {currentPowerKw} kW)
                                    </span>
                                )}
                            </li>
                        )}
                        {result.data.top_speed_kmh && <li><span className="text-gray-500">Vmax (T):</span> <span className="font-bold font-mono-num">{result.data.top_speed_kmh} km/h</span></li>}
                        {result.data.typical_consumption && <li><span className="text-gray-500">Verbrauch:</span> <span className="font-bold font-mono-num">{result.data.typical_consumption}</span></li>}
                        {result.data.emission_class && <li><span className="text-gray-500">Schadstoff (14):</span> <span className="font-bold">{result.data.emission_class}</span></li>}
                        {result.data.vehicle_class && <li><span className="text-gray-500">Klasse (J):</span> <span className="font-bold">{result.data.vehicle_class}</span></li>}
                    </ul>
                    {Array.isArray(result.data.common_options) && result.data.common_options.length > 0 && (
                        <div className="text-[11px] text-gray-600 mb-2">
                            <span className="font-bold">Häufige Werks-Optionen:</span> {result.data.common_options.join(" · ")}
                        </div>
                    )}
                    <Button size="sm" onClick={apply} className="bg-green-600 hover:bg-green-700 text-white" data-testid="kba-apply-btn">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Übernehmen
                    </Button>
                </div>
            )}
        </div>
    );
}
